import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Form,
  FormItem,
  Input,
  InputGroup,
} from "@clarkinc/zest-design-system/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { validatePhoneNumber } from "@wss/utilities/form";
import handleTextMessageSubmission from "~/common/ProductPromotion/handleTextMessageSubmission";

interface Props {
  itemNumber: string;
  billingPhoneNumber?: string;
}

const formSchema = z.object({
  phoneNumber: z
    .string()
    .nonempty()
    .transform(validatePhoneNumber("Please enter a valid phone number."))
    .transform((phone) => phone.toString()),
});

const PriceByTextForm = ({ itemNumber, billingPhoneNumber = "" }: Props) => {
  const alertRef = useRef<HTMLDivElement>(null);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneNumber: billingPhoneNumber ?? "",
    },
  });

  useEffect(() => {
    if (isSuccessful && alertRef.current) {
      alertRef.current.focus();
    }
  }, [isSuccessful]);

  useEffect(() => {
    const phoneInput = document.querySelector(".zest-input-wrapper");
    if (phoneInput) {
      phoneInput.classList.add("w-full");
    }
  }, []);

  useEffect(() => {
    if (billingPhoneNumber) {
      form.reset({ phoneNumber: billingPhoneNumber });
    }
  }, [billingPhoneNumber, form]);

  const handleSubmit = form.handleSubmit(async ({ phoneNumber }) => {
    try {
      const result = await handleTextMessageSubmission({
        phoneNumber,
        itemNumber,
      });

      if (!result.isError) {
        setIsSuccessful(true);
      } else {
        form.setError("phoneNumber", {
          type: "manual",
          message:
            result.error.message ?? "There was an error submitting the form.",
        });
      }
    } catch {
      form.setError("phoneNumber", {
        type: "manual",
        message: "Unexpected error. Please try again.",
      });
    }
  });

  return (
    <>
      {!isSuccessful && (
        <div className="input-append">
          <Form
            aria-label="Price by Text Form"
            data-component="getPriceByText"
            data-testid="getPriceByText"
            form={form}
            onSubmit={handleSubmit}
            theme="wss"
          >
            <InputGroup
              errorMessages={form.formState.errors.phoneNumber?.message}
              groupLabel="Get Price by Text"
              hasError={!!form.formState.errors.phoneNumber}
              id="textPrice"
              inputSize="medium"
              theme="wss"
              className="input-group w-full"
            >
              <div className="flex flex-row items-start text-left w-full">
                <FormItem name="phoneNumber">
                  <Input
                    aria-label="Enter phone number to get price by text"
                    autoComplete="on"
                    className="w-full rounded-r-none border-r-0"
                    data-testid="phone-number-input"
                    format
                    hideError
                    label="Get price by text"
                    type="tel"
                    value={form.watch("phoneNumber")}
                    onValueChange={(value) =>
                      form.setValue("phoneNumber", value)
                    }
                  />
                </FormItem>
                <Button
                  className="border border-solid border-gray-500 rounded-l-none shadow-none"
                  color="gray"
                  size="medium"
                  theme="wss"
                  type="submit"
                >
                  Text Price
                </Button>
              </div>
              <InputGroup.Error className="form__error" />
            </InputGroup>
          </Form>
        </div>
      )}
      <Alert
        className="w-auto mb-4"
        isOpen={isSuccessful}
        ref={alertRef}
        theme="wss"
        variant="success"
        assertive
      >
        <AlertTitle className="text-xs leading-tight">Thank you!</AlertTitle>
        <p className="text-xs leading-tight mb-0">
          A message has been sent to the contact information you provided.
        </p>
      </Alert>
    </>
  );
};

export default PriceByTextForm;
